import { NetworkErrorCode } from './../enums/NetworkErrorCode';
import { AuthService } from './../services/AuthService';
import { useSnackbar } from './../../stores/snackbar';
import { ApiTraceError } from '@/data/interfaces/ApiTraceError';
import { AxiosError } from 'axios';
import { i18n } from '@/plugins/i18n'

export class NetworkError {
  // #region Class properties
  private readonly error!: AxiosError;
  private readonly snackbar = useSnackbar();
  // #endregion

  constructor(error: AxiosError) {
    this.error = error;
  }

  // #region public methods
  public async handle(): Promise<void> {
    console.error(`NetworkError: ${this.error}`);

    switch (this.status) {
      case undefined:
      case 0:
        return this.handleUnknownError();
      case 401:
        return await this.handleUnauthorized();
      case 403:
        return await this.handleForbidden();
      case 400:
        return await this.handleBadRequest();
      case 423:
        return await this.handleAccountLocked();
      default:
        this.displayErrorToUser();
    }
  }
  // #endregion

  // #region private methods
  private handleUnknownError(): void {
    const t = i18n.global.t;
    this.snackbar.setSnackbar({
      title: t('unknown_error'),
      message: this.message || t('unknown_error_message'),
    });
  }
  private async handleAccountLocked(): Promise<void> {
    await useRouter().push('/423');
  }
  private async handleBadRequest(): Promise<void> {
    this.displayErrorToUser();
  }

  private async handleUnauthorized(): Promise<void> {
    const t = i18n.global.t;
    this.snackbar.setSnackbar({
      title: t('forbidden'),
      message: t('data_forbidden')
    });
  }

  private async handleForbidden(): Promise<void> {
    switch (this.errorCode) {

      case NetworkErrorCode.PRIVACY_POLICY:
      case NetworkErrorCode.TERMS_AND_CONDITIONS:
        return await this.handleAccountSetup();
      case NetworkErrorCode.USER_EXPIRED:
        return await this.handleExpiredUser();

      default:
        return await this.handleUnauthorized();
    }
  }

  private async handleAccountSetup(): Promise<void> {
    await useRouter().push('/account-setup');
  }

  private async handleExpiredUser(): Promise<void> {
    await useRouter().push(`/account-error?${NetworkErrorCode.USER_EXPIRED}`);
  }

  private displayErrorToUser(): void {
    this.snackbar.setSnackbar({
      title: `Error: ${this.status}`,
      message: this.message,
    });
  }
  // #endregion

  // #region getters
  private get status(): number | undefined {
    return this.error?.response?.status;
  }

  private get message(): string {
    return (
      (this.error?.response?.data as ApiTraceError)?.detail ||
      (this.error?.response?.data as ApiTraceError)?.title ||
      this.error?.response?.statusText ||
      '');
  }


  private get errorCode(): string | null {
    return (this.error?.response?.data as ApiTraceError)?.errorCode || null;
  }
  // #endregion
}
